import { Box, TableCell as MuiTableCell } from '@mui/material';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableSortableColumn,
  DataTableVariant,
} from './TableTypes';
import { colors, Typography } from '@sweep-io/sweep-design';
import classNames from 'classnames';
import { dataTableVariants } from './dataTableVariants';
import { ReactNode, useRef } from 'react';
import { getTableDividerCss } from './utils';
import { CELL_MIN_WIDTH, ResizeHandle } from './custom-columns-table/ResizeHandle';
import { useCustomColumnsTableData } from './custom-columns-table/CustomColumnsTableContext';

interface RowProps<TRow extends DataTableRow> {
  row: TRow;
  columns: DataTableSortableColumn<TRow>[] | DataTableBaseColumn[];
  onClick?: (
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    columnData: DataTableSortableColumn<TRow> | DataTableBaseColumn,
  ) => void;
  variant: DataTableVariant;
  dragHandle?: ReactNode;
  allowReorder?: boolean;
  isRenderEmpty: boolean;
  overColumnId?: string | null;
  draggedColumnId?: string | null;
  isColumnReorderAllowed?: boolean;
  isColumnResizeAllowed?: boolean;
  internalColumnWidths?: Record<string, number>;
}

function TableRowCells<TRow extends DataTableRow = any>({
  row,
  columns,
  onClick,
  variant,
  dragHandle,
  allowReorder,
  isRenderEmpty,
  overColumnId,
  draggedColumnId,
  isColumnReorderAllowed,
  isColumnResizeAllowed,
  internalColumnWidths,
}: RowProps<TRow>) {
  const cellRef = useRef<HTMLTableCellElement | null>(null);

  const { columnWidths, resizingColumnId } = useCustomColumnsTableData();

  const fontColor = row.disabled
    ? dataTableVariants[variant].disabledFontColor
    : dataTableVariants[variant].fontColor;

  return columns.map((column, index) => {
    const cell = column.renderer
      ? column.renderer(row[column.field], row.id, column.field, row)
      : row[column.field];
    const isPlainText = typeof cell === 'string';
    const withHandle = allowReorder && index === 0;
    const withDivider = index < columns.length - 1;
    const isOver =
      overColumnId === column.field && draggedColumnId !== column.field && draggedColumnId !== null;

    const cellContent =
      isPlainText && !column.renderer ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
          }}
          justifyContent={column.justifyContent}
        >
          <Typography variant={dataTableVariants[variant].fontVariant} color={fontColor}>
            {cell}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            '& .MuiTypography-root': {
              color: fontColor,
            },
            '& .avatar .MuiTypography-root': {
              color: colors.blue[500],
            },
          }}
          justifyContent={column.justifyContent}
        >
          {cell}
        </Box>
      );

    const columnWidth = columnWidths?.[column.field]
      ? columnWidths[column.field] + 'px'
      : undefined;

    const cellWidthPx = columnWidth ?? column.width ?? internalColumnWidths?.[column.field];
    const cellMinWidthPx = columnWidth
      ? CELL_MIN_WIDTH
      : (column.minWidth ?? dataTableVariants[variant]?.minRowWidth);
    const cellMaxWidthPx = columnWidth ?? cellWidthPx;

    return (
      <MuiTableCell
        key={column.field}
        onClick={
          onClick
            ? (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
                if (resizingColumnId) {
                  e.stopPropagation();
                  return;
                }
                onClick(e, column);
              }
            : undefined
        }
        sx={{
          position: 'relative',
          '& .MuiTypography-root': {
            color: fontColor,
          },
          padding: dataTableVariants[variant].cellPadding,
          borderBottom: `1px solid ${colors.grey[120]}`,
          borderRight: isColumnReorderAllowed
            ? isOver
              ? `1px solid ${colors.blue[500]}`
              : `1px solid transparent`
            : `1px solid transparent`,
          width: cellWidthPx,
          minWidth: cellMinWidthPx,
          maxWidth: cellMaxWidthPx,
          cursor: !!onClick ? 'pointer' : 'default',
          ...(withDivider && {
            position: 'relative',
            ...getTableDividerCss(!!isOver, false),
          }),
        }}
        className={classNames(column.className, 'SweepDataTableCell')}
        ref={cellRef}
      >
        {!isRenderEmpty && withHandle && (
          <Box
            sx={{
              width: cellWidthPx,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'center',
              color: fontColor,
              '& .MuiTypography-root': {
                color: fontColor,
              },
            }}
          >
            {!resizingColumnId && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'grab',
                }}
              >
                <Box display="flex">{dragHandle}</Box>
              </Box>
            )}
            {cellContent}
            {isColumnResizeAllowed && <ResizeHandle column={column} cellRef={cellRef} />}
          </Box>
        )}

        {!isRenderEmpty && !withHandle && (
          <Box
            display="flex"
            alignItems="center"
            alignContent="center"
            justifyContent="space-between"
          >
            <Box
              sx={{
                minWidth: cellMinWidthPx
                  ? 0
                  : (column.minWidth ?? dataTableVariants[variant]?.minRowWidth),
                width: cellWidthPx,
              }}
            >
              {cellContent}
              {isColumnResizeAllowed && <ResizeHandle column={column} cellRef={cellRef} />}
            </Box>
          </Box>
        )}
      </MuiTableCell>
    );
  });
}

export default TableRowCells;
